import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { ExternalLink, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { formatSessionDate } from '@/lib/datetime';
import { ApiError } from '@/lib/api';
import { CaseAudioPanel } from '@/pages/cade/CaseAudioPanel';
import { CodingsPanel } from '@/pages/cade/CodingsPanel';
import { JudicialPanel } from '@/pages/cade/JudicialPanel';
import { PlacementCard } from '@/pages/cade/PositioningPage';
import { fetchCadeDossier } from '@/services/cade';
import type {
  CadeCaseEvent,
  CadeDossier,
  CadeDossierHistoryRow,
  CadeDossierPauta,
  CadeDossierRegister,
  CadeDossierRelator,
  CadeOutcomeShare,
} from '@/services/types';

/**
 * One case laid out for the day before its session.
 *
 * Four sections, each stating what it is and is not: the case's own history in the archive, the
 * relator's final decisions in the same case type beside every relator's (a historical
 * association over cases that differ in facts, never a forecast, shown as shares only above a
 * floor), the published register's row, and where fines fell among similar protocols. What was
 * said about the case in earlier recorded sessions is the same panel the case page carries.
 */
export function CadeDossierPage() {
  const { t } = useTranslation(['cade']);
  const { key = '' } = useParams<{ key: string }>();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'dossier', key],
    queryFn: () => fetchCadeDossier(key),
    retry: false,
  });

  if (isPending) {
    return (
      <div className="p-6">
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <ErrorMessage
          message={notFound ? t('cade:dossier.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }
  return <DossierBody data={data} />;
}

function DossierBody({ data }: { data: CadeDossier }) {
  const { t, i18n } = useTranslation(['cade']);
  const money = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  return (
    <div className="space-y-5 p-6" data-dossier>
      <header className="space-y-1">
        <p className="text-xs uppercase text-muted-foreground">{t('cade:dossier.title')}</p>
        <h1 className="text-lg font-semibold tabular-nums text-foreground">{data.case_number}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:dossier.subtitle')}</p>
        {data.pauta ? <PautaLine pauta={data.pauta} /> : null}
        <div className="flex flex-wrap items-center gap-4">
          {data.in_archive ? (
            <Link
              to={`/cade/c/${data.case_key}`}
              className="text-sm text-primary underline underline-offset-4"
            >
              {t('cade:dossier.openCase')}
            </Link>
          ) : null}
          {/* The browser's own print dialog saves a PDF; the print stylesheet lays it out. */}
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-foreground print:hidden"
            data-dossier-print
          >
            <Printer className="h-3.5 w-3.5" aria-hidden />
            {t('cade:dossier.print')}
          </button>
        </div>
      </header>
      <AsOfStrip asOf={data.as_of} />

      {(data.timeline?.length ?? 0) > 0 ? <Timeline events={data.timeline ?? []} /> : null}

      <section className="space-y-2" data-dossier-history>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:dossier.historyTitle')}</h2>
        <Answer answer={data.history} help={t('cade:dossier.historyHelp')} scope="record">
          {(rows: CadeDossierHistoryRow[]) => <HistoryTable rows={rows} />}
        </Answer>
      </section>

      <section className="space-y-2" data-dossier-relator>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:dossier.relatorTitle')}</h2>
        <Answer answer={data.relator} help={t('cade:dossier.relatorHelp')} scope="record">
          {(record: CadeDossierRelator) => <RelatorBlock record={record} />}
        </Answer>
      </section>

      <section className="space-y-2" data-dossier-register>
        <h2 className="text-sm font-semibold text-foreground">{t('cade:dossier.registerTitle')}</h2>
        <Answer answer={data.register_row} help={t('cade:dossier.registerHelp')} scope="record">
          {(record: CadeDossierRegister) => (
            <RegisterBlock record={record} money={(n) => money.format(n)} />
          )}
        </Answer>
      </section>

      <section className="space-y-2" data-dossier-placement>
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:dossier.placementTitle')}
        </h2>
        <Answer answer={data.placement} help={t('cade:dossier.placementHelp')} scope="record">
          {(placement) => <PlacementCard placement={placement} money={(n) => money.format(n)} />}
        </Answer>
      </section>

      {(data.parties?.length ?? 0) > 0 ? <Parties parties={data.parties ?? []} /> : null}

      {data.codings ? <CodingsPanel caseKey={data.case_key} codings={data.codings} /> : null}

      <JudicialPanel caseKey={data.case_key} />

      {data.in_archive ? <CaseAudioPanel caseKey={data.case_key} /> : null}
    </div>
  );
}

function PautaLine({ pauta }: { pauta: CadeDossierPauta }) {
  const { t, i18n } = useTranslation(['cade']);
  const kind =
    pauta.session_kind === 'EXTRAORDINARY'
      ? t('cade:pautas.extraordinary')
      : t('cade:pautas.ordinary');
  const session =
    pauta.session_number !== null
      ? t('cade:pautas.session', { number: pauta.session_number, kind })
      : t('cade:pautas.sessionNoNumber', { kind });
  return (
    <p className="text-sm text-foreground" data-dossier-pauta>
      {t('cade:dossier.pauta', {
        session,
        date: formatSessionDate(pauta.session_date, null, i18n.language) ?? pauta.session_date,
        relator: pauta.relator ?? t('cade:dossier.noRelatorYet'),
      })}
      {pauta.standing ? ` · ${t(`cade:pautas.standing.${pauta.standing}`)}` : ''}
    </p>
  );
}

function outcomeLabels(codes: string[], t: (k: string, o?: Record<string, unknown>) => string) {
  return codes
    .map((code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code }))
    .join(', ');
}

function HistoryTable({ rows }: { rows: CadeDossierHistoryRow[] }) {
  const { t, i18n } = useTranslation(['cade']);
  return (
    <div className="overflow-auto rounded-lg border">
      <table className="w-full text-sm">
        <caption className="sr-only">{t('cade:dossier.historyTitle')}</caption>
        <thead className="bg-muted text-left text-xs text-foreground">
          <tr>
            <th scope="col" className="px-3 py-2">
              {t('cade:dossier.col.session')}
            </th>
            <th scope="col" className="px-3 py-2">
              {t('cade:dossier.col.outcome')}
            </th>
            <th scope="col" className="px-3 py-2">
              {t('cade:dossier.col.relator')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.session_date}-${row.item_number}-${index}`}
              className="border-t align-top"
            >
              <td className="px-3 py-1.5 tabular-nums text-foreground">
                {formatSessionDate(row.session_date, null, i18n.language) ?? '—'}
                {row.session_number ? (
                  <span className="ml-1 text-xs text-muted-foreground">#{row.session_number}</span>
                ) : null}
              </td>
              <td className="px-3 py-1.5 text-foreground">
                {outcomeLabels(row.outcomes, t) || '—'}
                {row.view_request ? (
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs" data-vista>
                    {row.requester
                      ? t('cade:dossier.vistaBy', { name: row.requester })
                      : t('cade:dossier.vista')}
                  </span>
                ) : null}
              </td>
              <td className="px-3 py-1.5 text-muted-foreground">{row.relator ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function pct(count: number, total: number): string {
  return total > 0 ? `${Math.round((count / total) * 100)}%` : '—';
}

function RelatorBlock({ record }: { record: CadeDossierRelator }) {
  const { t, i18n } = useTranslation(['cade']);
  const codes = Array.from(
    new Set([...record.relator_outcomes, ...record.corpus_outcomes].map((o) => o.code)),
  );
  const own = new Map(record.relator_outcomes.map((o: CadeOutcomeShare) => [o.code, o.count]));
  const all = new Map(record.corpus_outcomes.map((o: CadeOutcomeShare) => [o.code, o.count]));
  codes.sort((a, b) => (all.get(b) ?? 0) - (all.get(a) ?? 0) || a.localeCompare(b));
  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground" data-relator-summary>
        {t('cade:dossier.relatorSummary', {
          relator: record.relator,
          n: record.relator_finals,
          type: record.case_type_label,
          all: record.corpus_finals,
        })}
      </p>
      {record.shares_shown ? (
        <div className="overflow-auto rounded-lg border" data-relator-shares>
          <table className="w-full text-sm">
            <caption className="sr-only">{t('cade:dossier.relatorTitle')}</caption>
            <thead className="bg-muted text-left text-xs text-foreground">
              <tr>
                <th scope="col" className="px-3 py-2">
                  {t('cade:dossier.col.outcome')}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t('cade:dossier.col.thisRelator')}
                </th>
                <th scope="col" className="px-3 py-2">
                  {t('cade:dossier.col.everyRelator')}
                </th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code} className="border-t">
                  <td className="px-3 py-1.5 text-foreground">
                    {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
                  </td>
                  <td className="px-3 py-1.5 tabular-nums text-foreground">
                    {own.get(code) ?? 0} ({pct(own.get(code) ?? 0, record.relator_finals)})
                  </td>
                  <td className="px-3 py-1.5 tabular-nums text-foreground">
                    {all.get(code) ?? 0} ({pct(all.get(code) ?? 0, record.corpus_finals)})
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-relator-withheld>
          {t('cade:dossier.sharesWithheld', { min: record.min_finals })}
        </p>
      )}
      <div className="space-y-1">
        <h3 className="text-xs font-semibold text-foreground">{t('cade:dossier.casesTitle')}</h3>
        <ul className="space-y-1 text-sm" data-relator-cases>
          {record.cases.map((c) => (
            <li key={`${c.case_key}-${c.session_date}`} className="text-foreground">
              <Link
                to={`/cade/c/${c.case_key}`}
                className="text-primary underline underline-offset-4"
              >
                {c.case_number}
              </Link>{' '}
              <span className="text-xs text-muted-foreground">
                {formatSessionDate(c.session_date, null, i18n.language) ?? ''}
                {c.outcomes.length ? ` · ${outcomeLabels(c.outcomes, t)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function RegisterBlock({
  record,
  money,
}: {
  record: CadeDossierRegister;
  money: (n: number) => string;
}) {
  const { t } = useTranslation(['cade']);
  const fields: [string, string | null][] = [
    ['procedure', record.procedure],
    [
      'conducts',
      record.conducts.length
        ? record.conducts.map((c) => t(`cade:positioning.conducts.${c}`)).join(', ')
        : null,
    ],
    ['sg', record.sg_outcome],
    ['tribunal', record.tribunal_outcome],
    ['year', record.decision_year !== null ? String(record.decision_year) : null],
    ['fine', record.fine_brl !== null ? money(record.fine_brl) : null],
    ['contribution', record.contribution_brl !== null ? money(record.contribution_brl) : null],
  ];
  return (
    <dl className="grid grid-cols-1 gap-2 rounded-md border p-3 sm:grid-cols-2" data-register>
      {fields.map(([name, value]) => (
        <div key={name}>
          <dt className="text-xs text-muted-foreground">{t(`cade:dossier.register.${name}`)}</dt>
          <dd className="text-sm text-foreground">{value ?? t('cade:dossier.register.none')}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One label for an event, from the vocabulary of the source that dated it. */
function eventLabel(event: CadeCaseEvent, t: TFunction): string {
  if (event.source === 'register') {
    return t(`cade:case.register.kinds.${event.kind}`, { defaultValue: event.kind });
  }
  if (event.source === 'sei') {
    return t(`cade:case.sei.kinds.${event.kind}`, { defaultValue: event.detail ?? event.kind });
  }
  return t(`cade:dossier.timeline.kinds.${event.kind}`, { defaultValue: event.kind });
}

/**
 * The case's steps in one list, merged from the register, SEI and the session archive. The
 * sources are not reconciled: one judgment can show as a register date, an SEI certificate and an
 * archive item, and each row says which source dates it.
 */
function Timeline({ events }: { events: CadeCaseEvent[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const day = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' });
  return (
    <section className="space-y-2" data-dossier-timeline>
      <h2 className="text-sm font-semibold text-foreground">{t('cade:dossier.timeline.title')}</h2>
      <p className="text-xs text-muted-foreground">{t('cade:dossier.timeline.help')}</p>
      <ol className="space-y-1 border-l pl-4 text-sm">
        {events.map((event, index) => (
          <li key={`${event.source}-${event.kind}-${event.when}-${index}`} data-event-source={event.source}>
            <span className="tabular-nums text-foreground">
              {event.when ? day.format(new Date(`${event.when}T00:00:00Z`)) : '—'}
            </span>
            <span className="ml-2 text-foreground">{eventLabel(event, t)}</span>
            {event.source === 'archive' && event.session_number ? (
              <span className="ml-2 text-xs text-muted-foreground">
                {t('cade:dossier.timeline.session', { number: event.session_number })}
              </span>
            ) : null}
            {event.requester ? (
              <span className="ml-2 text-xs text-muted-foreground">
                {t('cade:dossier.timeline.requester', { name: event.requester })}
              </span>
            ) : null}
            {event.source === 'archive' && event.detail ? (
              <span className="ml-2 text-xs text-muted-foreground">
                {event.detail
                  .split('; ')
                  .map((code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code }))
                  .join('; ')}
              </span>
            ) : null}
            {event.source === 'register' && event.detail ? (
              <span className="ml-2 text-xs text-muted-foreground">{event.detail}</span>
            ) : null}
            <span className="ml-2 rounded bg-muted px-1.5 text-xs text-foreground">
              {t(`cade:dossier.timeline.sources.${event.source}`)}
            </span>
            {event.url ? (
              <a
                href={event.url}
                target="_blank"
                rel="noreferrer"
                className="ml-2 inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
              >
                SEI
                <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** The parties the archive names on the case, each with any CNPJ linked on evidence. */
function Parties({ parties }: { parties: NonNullable<CadeDossier['parties']> }) {
  const { t } = useTranslation(['cade']);
  return (
    <section className="space-y-2" data-dossier-parties>
      <h2 className="text-sm font-semibold text-foreground">{t('cade:dossier.parties.title')}</h2>
      <p className="text-xs text-muted-foreground">{t('cade:dossier.parties.help')}</p>
      <ul className="grid gap-1 text-sm sm:grid-cols-2">
        {parties.map((party) => (
          <li key={party.name}>
            <span className="text-foreground">{party.name}</span>
            <span className="ml-2 text-xs text-muted-foreground">
              {party.roles
                .map((role) => t(`cade:parties.roles.${role}`, { defaultValue: role }))
                .join(', ')}
            </span>
            {party.cnpj ? (
              <Link
                to={`/cade/company/${party.cnpj.slice(0, 8)}`}
                className="ml-2 text-xs tabular-nums text-primary underline-offset-4 hover:underline"
              >
                {party.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

